import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPolicy,
  generatePolicy,
  getPolicy,
  listPolicies,
  listPolicyVersions,
  setPolicyActive,
  updatePolicy,
} from './api';
import type { CreatePolicyInput, Policy, UpdatePolicyInput } from './types';

const ROOT = ['policies'] as const;

export function usePolicies(params: { active?: boolean } = {}) {
  return useQuery({
    queryKey: [...ROOT, 'list', params] as const,
    queryFn: () => listPolicies(params),
    staleTime: 30_000,
  });
}

export function usePolicy(policyUrn: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, policyUrn ?? ''] as const,
    queryFn: () => getPolicy(policyUrn as string),
    enabled: Boolean(policyUrn),
  });
}

export function usePolicyVersions(policyUrn: string | undefined) {
  return useQuery({
    queryKey: [...ROOT, policyUrn ?? '', 'versions'] as const,
    queryFn: () => listPolicyVersions(policyUrn as string),
    enabled: Boolean(policyUrn),
  });
}

export function useCreatePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePolicyInput) => createPolicy(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROOT });
    },
  });
}

export function useUpdatePolicy(policyUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePolicyInput) => updatePolicy(policyUrn, input),
    onSuccess: (updated: Policy) => {
      qc.setQueryData([...ROOT, policyUrn], updated);
      void qc.invalidateQueries({ queryKey: [...ROOT, policyUrn, 'versions'] });
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useGeneratePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prompt: string) => generatePolicy(prompt),
    // generate-ai persists an inactive draft server-side — seed its detail
    // cache and refresh the list so the new row shows up.
    onSuccess: (created: Policy) => {
      qc.setQueryData([...ROOT, created.policyUrn], created);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}

export function useSetPolicyActive(policyUrn: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (active: boolean) => setPolicyActive(policyUrn, active),
    onSuccess: (updated: Policy) => {
      qc.setQueryData([...ROOT, policyUrn], updated);
      void qc.invalidateQueries({ queryKey: [...ROOT, 'list'] });
    },
  });
}
